import * as yup from "yup";
import {
  CONDITIONS,
  GENDER,
  MARTIAL_STATUSES,
  POPULATION_FOCUSES,
  RACES,
  US_STATES,
} from "constants/common";
import Input from "components/common/input";
import { memberInitialValues, memberSchema } from "constants/members";
import { ErrorMessage, Formik, useFormikContext } from "formik";
import SelectComponent from "components/common/select";
import DateInput from "components/common/date-picker";
import {
  allConditions,
  normalizeInput,
  initialMetaForTable,
} from "constants/common";
import AsyncSelect from "components/common/select/AsyncSelect";
import { useCallback, useEffect, useState } from "react";
import {
  getCities,
  getCounties,
  getHealthPlans,
  getStates,
} from "containers/policies/api";
import {
  useDebouncedCitiesDataCallback,
  useDebouncedCountiesDataCallback,
  useDebouncedHealthPlansDataCallback,
  useDebouncedStatesDataCallback,
  useDebouncedZipCodesDataCallback,
} from "utils/debouncedHelper";
import {
  capitalizeName,
  disableFutureDates,
  formatLocation,
} from "utils/common";
import { getZipCodes } from "containers/leadcaremanager/api";
import { toast } from "react-toastify";
import CustomToast from "components/common/custom-toast";
import { alertTypes } from "constants/common";
const AddressSync = ({
  isMailingSameAsHome,
  fetchMailingCounties,
  fetchMailingCities,
  fetchMailingZipCodes,
  mailingCountiesList,
  mailingCitiesList,
  mailingZipCodesList,
}) => {
  const { values, setFieldValue } = useFormikContext();

  const syncMailingAddress = () => {
    setFieldValue("mailing_address", values.home_address);
    setFieldValue("mailing_state_id", values.state_id);
    setFieldValue("mailing_county_id", values.county_id);
    setFieldValue("mailing_city_id", values.city_id);
    setFieldValue("mailing_zip_code_id", values.zip_code_id);

    if (values.state_id?.value && mailingCountiesList.length === 0) {
      fetchMailingCounties(values.state_id.value);
    }
    if (
      values.state_id?.value &&
      values.county_id?.value &&
      mailingCitiesList.length === 0
    ) {
      fetchMailingCities(values.state_id.value, values.county_id.value);
    }
    if (
      values.state_id?.value &&
      values.county_id?.value &&
      values.city_id?.value &&
      mailingZipCodesList.length === 0
    ) {
      fetchMailingZipCodes(
        values.state_id.value,
        values.county_id.value,
        values.city_id.value
      );
    }
  };

  useEffect(() => {
    if (isMailingSameAsHome) {
      syncMailingAddress();
    }
  }, [
    isMailingSameAsHome,
    values.home_address,
    values.state_id,
    values.county_id,
    values.city_id,
    values.zip_code_id,
    mailingCountiesList,
    mailingCitiesList,
    mailingZipCodesList,
  ]);

  return null;
};

const AddQuestionForm = ({
  handleQuestionSubmittion,
  handleClose,
  selectedMember,
}) => {
  const [healthPlansList, setHealthPlansList] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [countiesList, setCountiesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [zipCodesList, setZipCodesList] = useState([]);
  const [mailingStatesList, setMailingStatesList] = useState([]);
  const [mailingCountiesList, setMailingCountiesList] = useState([]);
  const [mailingCitiesList, setMailingCitiesList] = useState([]);
  const [mailingZipCodesList, setMailingZipCodesList] = useState([]);
  const [isMailingSameAsHome, setIsMailingSameAsHome] = useState(false);

  const mailingAddress = selectedMember?.addresses?.find(
    (addr) => addr.address_type === "mailing"
  );
  const homeAddress = selectedMember?.addresses?.find(
    (addr) => addr.address_type === "home"
  );

  const fetchStates = async () => {
    const result = await getStates({ ...initialMetaForTable });
    if (result["states"]) {
      const data = result?.states;
      setStatesList(data);
      setMailingStatesList(data);
    }
  };

  const fetchCounties = async (stateId) => {
    const result = await getCounties({
      ...initialMetaForTable,
      state_id: stateId,
    });
    if (result["counties"]) {
      setCountiesList(result.counties);
    }
  };

  const fetchCities = async (stateId, countyId) => {
    const result = await getCities({
      ...initialMetaForTable,
      state_id: stateId,
      county_id: countyId,
    });
    if (result["cities"]) {
      setCitiesList(result.cities);
    }
  };

  const fetchZipCodes = async (stateId, countyId, cityId) => {
    const result = await getZipCodes({
      ...initialMetaForTable,
      state_id: stateId,
      county_id: countyId,
      city_id: cityId,
    });
    if (result["zip_codes"]) {
      setZipCodesList(result.zip_codes);
    }
  };

  const fetchMailingCounties = async (stateId) => {
    const result = await getCounties({
      ...initialMetaForTable,
      state_id: stateId,
    });
    if (result["counties"]) {
      setMailingCountiesList(result.counties);
    }
  };

  const fetchMailingCities = async (stateId, countyId) => {
    const result = await getCities({
      ...initialMetaForTable,
      state_id: stateId,
      county_id: countyId,
    });
    if (result["cities"]) {
      setMailingCitiesList(result.cities);
    }
  };

  const fetchMailingZipCodes = async (stateId, countyId, cityId) => {
    const result = await getZipCodes({
      ...initialMetaForTable,
      state_id: stateId,
      county_id: countyId,
      city_id: cityId,
    });
    if (result["zip_codes"]) {
      setMailingZipCodesList(result.zip_codes);
    }
  };

  const fetchHealthPlan = async (searchString) => {
    const result = await getHealthPlans({
      ...initialMetaForTable,
      search: searchString,
    });
    if (result["health_plans"]) {
      setHealthPlansList(result.health_plans);
    }
  };

  useEffect(() => {
    fetchHealthPlan();
    fetchStates();
  }, []);

  useEffect(() => {
    if (selectedMember?.state?.id) {
      fetchCounties(selectedMember.state.id);
    }
  }, [selectedMember]);

  useEffect(() => {
    if (selectedMember?.state?.id && selectedMember?.county?.id) {
      fetchCities(selectedMember.state.id, selectedMember.county.id);
    }
  }, [selectedMember]);

  useEffect(() => {
    if (
      selectedMember?.state?.id &&
      selectedMember?.county?.id &&
      selectedMember?.city?.id
    ) {
      fetchZipCodes(
        selectedMember.state.id,
        selectedMember.county.id,
        selectedMember?.city?.id
      );
    }
  }, [selectedMember]);

  useEffect(() => {
    if (selectedMember && mailingAddress?.state_id) {
      fetchMailingCounties(mailingAddress.state_id);
    }
  }, [selectedMember, mailingAddress]);

  useEffect(() => {
    if (
      selectedMember &&
      mailingAddress?.state_id &&
      mailingAddress?.county_id
    ) {
      fetchMailingCities(mailingAddress.state_id, mailingAddress.county_id);
    }
  }, [selectedMember, mailingAddress]);

  useEffect(() => {
    if (
      selectedMember &&
      mailingAddress?.state_id &&
      mailingAddress?.county_id &&
      mailingAddress?.city_id
    ) {
      fetchMailingZipCodes(
        mailingAddress.state_id,
        mailingAddress.county_id,
        mailingAddress.city_id
      );
    }
  }, [selectedMember, mailingAddress]);
  useEffect(() => {
    if (
      mailingAddress?.street === homeAddress?.street &&
      mailingAddress?.state === homeAddress?.state &&
      mailingAddress?.county === homeAddress?.county &&
      selectedMember
    ) {
      setIsMailingSameAsHome(true);
    }
  }, [selectedMember]);

  const debouncedHealthPlansDataCallback = useDebouncedHealthPlansDataCallback();
  const debouncedStatesDataCallback = useDebouncedStatesDataCallback();
  const debouncedCountiesDataCallback = useDebouncedCountiesDataCallback();
  const debouncedCitiesDataCallback = useDebouncedCitiesDataCallback();
  const debouncedZipCodesDataCallback = useDebouncedZipCodesDataCallback();

  return (
    <Formik
      initialValues={memberInitialValues(selectedMember)}
      validationSchema={memberSchema}
      enableReinitialize
      onSubmit={(values, { setErrors }) => {
        const {
          home_address,
          city_id,
          state_id,
          county_id,
          zip_code_id,
          mailing_address,
          mailing_city_id,
          mailing_state_id,
          mailing_county_id,
          mailing_zip_code_id,
          population_focus,
          conditions,
          ...filteredValues
        } = values;

        const addresses_attributes = [
          {
            ...(homeAddress?.id && { id: homeAddress.id }),
            street: home_address,
            city: city_id?.label || "",
            state: state_id?.label || "",
            county: county_id?.label || "",
            zip_code: zip_code_id?.label || "",
            zip_code_id: zip_code_id?.value || "",
            city_id: city_id?.value || "",
            state_id: state_id?.value || "",
            county_id: county_id?.value || "",
            address_type: "home",
          },
          {
            ...(mailingAddress?.id && { id: mailingAddress.id }),
            street: mailing_address,
            city: mailing_city_id?.label || "",
            state: mailing_state_id?.label || "",
            county: mailing_county_id?.label || "",
            zip_code: mailing_zip_code_id?.label || "",
            city_id: mailing_city_id?.value || "",
            state_id: mailing_state_id?.value || "",
            county_id: mailing_county_id?.value || "",
            zip_code_id: mailing_zip_code_id?.value || "",
            address_type: "mailing",
          },
        ];

        handleQuestionSubmittion(
          {
            member: {
              ...filteredValues,
              addresses_attributes,
              city_id: city_id?.value || "",
              state_id: state_id?.value || "",
              county_id: county_id?.value || "",
              zip_code_id: zip_code_id?.value || "",
              population_focus: population_focus
                .map((item) => item.value)
                .join(","),
              conditions: conditions.map((item) => item.value).join(","),
            },
          },
          setErrors
        );
      }}
    >
      {({ handleChange, values, handleSubmit, errors, setFieldValue }) => (
        <div className="question-modal">
          <form className="form-main" onSubmit={handleSubmit}>
            <hr className="custom-modal-line mt-0" />
            <h4 className="my-4">Personal Info</h4>
            <div className="input-flex row">
              <div className="col-sm-4">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. John Doe"
                  type="text"
                  label="First Name"
                  value={values.first_name}
                  name="first_name"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="first_name"
                    />
                  }
                />
              </div>
              <div className="col-sm-4">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. John Doe"
                  type="text"
                  label="Last Name"
                  value={values.last_name}
                  name="last_name"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="last_name"
                    />
                  }
                />
              </div>
              <div className="col-sm-4">
                <DateInput
                  isRequired
                  handleChange={(date) => setFieldValue("date_of_birth", date)}
                  value={values.date_of_birth}
                  label=" Date of Birth"
                  name="date_of_birth"
                  placeholder="Date of Birth"
                  isValidDate={disableFutureDates}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="date_of_birth"
                    />
                  }
                />
              </div>
            </div>
            <div className="input-flex row">
              <div className="col-sm-4">
                <SelectComponent
                  isRequired
                  label="Gender"
                  selectedValue={values.gender}
                  options={GENDER}
                  placeholder="Select Gender"
                  handleChange={(obj) => setFieldValue("gender", obj.value)}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="gender"
                    />
                  }
                />
              </div>
              <div className="col-sm-4">
                <SelectComponent
                  label="Race/Ethnicity"
                  options={RACES}
                  selectedValue={values.ehtnicity}
                  placeholder="Select Race/Ethnicity"
                  handleChange={(obj) => setFieldValue("ehtnicity", obj.value)}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="ehtnicity"
                    />
                  }
                />
              </div>
              <div className="col-sm-4">
                <SelectComponent
                  label="Marital Status"
                  options={MARTIAL_STATUSES}
                  selectedValue={values.martial_status}
                  placeholder="Select Marital Status"
                  handleChange={(obj) =>
                    setFieldValue("martial_status", obj.value)
                  }
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="martial_status"
                    />
                  }
                />
              </div>
            </div>
            <h4 className="my-4">Membership Info</h4>
            <div className="input-flex row">
              <div className="col-sm-6">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. CIN7438201"
                  type="text"
                  label="CIN"
                  value={values.cin}
                  name="cin"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="cin"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <Input
                  disabled={selectedMember}
                  handleChange={handleChange}
                  placeholder="e.g. AICA256849317"
                  type="text"
                  label="FHIR ID"
                  value={values.aica_account}
                  name="aica_account"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="aica_account"
                    />
                  }
                />
              </div>
            </div>
            <div className="input-flex row">
              <div className="col-sm-6">
                <AsyncSelect
                  label="Health Plan"
                  isRequired
                  options={
                    healthPlansList.find(
                      (el) => el.id === selectedMember?.health_plan_id
                    ) || typeof selectedMember?.health_plan_id === "undefined"
                      ? healthPlansList?.map((el) => ({
                          value: el.id,
                          label: el.name,
                        }))
                      : [
                          ...healthPlansList?.map((el) => ({
                            value: el.id,
                            label: el.name,
                          })),
                          {
                            value: selectedMember.health_plan?.id,
                            label: selectedMember.health_plan?.name,
                          },
                        ]
                  }
                  loadOptions={(inputValue) =>
                    new Promise((resolve) =>
                      debouncedHealthPlansDataCallback(inputValue, resolve)
                    )
                  }
                  selectedValue={values.health_plan_id}
                  placeholder="Select Health Plan"
                  handleChange={(obj) =>
                    setFieldValue("health_plan_id", obj.value)
                  }
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="health_plan_id"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <Input
                  handleChange={handleChange}
                  placeholder="e.g. Number"
                  type="text"
                  label="IF Number"
                  value={values.if_number}
                  name="if_number"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="if_number"
                    />
                  }
                />
              </div>
            </div>
            <h4 className="my-4">Contact Info</h4>
            <div className="input-flex row">
              <div className="col-sm-6">
                <Input
                  isRequired
                  handleChange={(e) => {
                    const formattedNumber = normalizeInput(e.target.value);
                    setFieldValue("phone_number", formattedNumber);
                  }}
                  placeholder="e.g. 555-123-4567"
                  type="text"
                  label="Phone"
                  value={values.phone_number}
                  name="phone_number"
                  className="mb-1"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="phone_number"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <Input
                  handleChange={handleChange}
                  placeholder="e.g. johndoe@gmail.com"
                  type="email"
                  label="Email"
                  value={values.email}
                  name="email"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="email"
                    />
                  }
                />
              </div>
            </div>
            <div className="input-flex row">
              <div className="col-sm-6">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. 1234 Main St."
                  type="text"
                  label="Home Address"
                  value={values.home_address}
                  name="home_address"
                  className="mb-1"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="home_address"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <AsyncSelect
                  label="Home State"
                  isRequired
                  options={
                    statesList.find(
                      (plan) => plan.id === selectedMember?.state_id
                    ) || typeof selectedMember?.state_id === "undefined"
                      ? statesList?.map((el) => ({
                          value: el.id,
                          label: el.name.toUpperCase(),
                        }))
                      : [
                          ...statesList?.map((el) => ({
                            value: el.id,
                            label: el.name,
                          })),
                          {
                            value: selectedMember.state?.id,
                            label: selectedMember.state?.name,
                          },
                        ]
                  }
                  loadOptions={(inputValue) =>
                    new Promise((resolve) =>
                      debouncedStatesDataCallback(inputValue, resolve)
                    )
                  }
                  selectedValue={values.state_id}
                  placeholder="Select State"
                  handleChange={(obj) => {
                    setFieldValue("state_id", obj);
                    setFieldValue("county_id", "");
                    setFieldValue("city_id", "");
                    setFieldValue("zip_code_id", "");
                    fetchCounties(obj.value);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="state_id"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <AsyncSelect
                  selectedValue={values.county_id}
                  label="Home County"
                  isRequired
                  disabled={!values.state_id}
                  options={
                    countiesList.find(
                      (plan) => plan.id === selectedMember?.county_id
                    ) || typeof selectedMember?.county_id === "undefined"
                      ? countiesList?.map((el) => ({
                          value: el.id,
                          label: formatLocation(el.name),
                        }))
                      : [
                          ...countiesList?.map((el) => ({
                            value: el.id,
                            label: el.name,
                          })),
                          {
                            value: selectedMember.county?.id,
                            label: selectedMember.county?.name,
                          },
                        ]
                  }
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedCountiesDataCallback(
                        inputValue,
                        values.state_id?.value,
                        resolve
                      );
                    })
                  }
                  placeholder="Select County"
                  handleChange={(obj) => {
                    setFieldValue("county_id", obj);
                    setFieldValue("city_id", "");
                    setFieldValue("zip_code_id", "");
                    fetchCities(values.state_id?.value, obj.value);
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="county_id"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <AsyncSelect
                  label="Home City"
                  disabled={!values.county_id}
                  options={citiesList.map((el) => ({
                    value: el.id,
                    label: capitalizeName(el.name),
                  }))}
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedCitiesDataCallback(
                        inputValue,
                        values.state_id?.value,
                        values.county_id?.value,
                        resolve
                      );
                    })
                  }
                  selectedValue={values.city_id}
                  placeholder="Select City"
                  handleChange={(obj) => {
                    setFieldValue("city_id", obj);
                    setFieldValue("zip_code_id", "");
                    fetchZipCodes(
                      values.state_id?.value,
                      values.county_id?.value,
                      obj.value
                    );
                  }}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="city_id"
                    />
                  }
                />
              </div>
              <div>
                <AsyncSelect
                  name="zip_code_id"
                  label="Home ZIP Code"
                  disabled={!values.city_id}
                  options={
                    zipCodesList.find(
                      (plan) => plan.id === selectedMember?.zip_code_id
                    ) || typeof selectedMember?.zip_code_id === "undefined"
                      ? zipCodesList?.map((el) => ({
                          value: el.id,
                          label: el.code,
                        }))
                      : [
                          ...zipCodesList?.map((el) => ({
                            value: el.id,
                            label: el.code,
                          })),
                          {
                            value: selectedMember.zip_code?.id,
                            label: selectedMember.zip_code?.code,
                          },
                        ]
                  }
                  loadOptions={(inputValue) =>
                    new Promise((resolve) => {
                      debouncedZipCodesDataCallback(
                        inputValue,
                        values.state_id?.value,
                        values.county_id?.value,
                        values.city_id?.value,
                        resolve
                      );
                    })
                  }
                  selectedValue={values.zip_code_id}
                  handleChange={(obj) => setFieldValue("zip_code_id", obj)}
                  className="mb-1"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="zip_code_id"
                    />
                  }
                />
              </div>
              {/* Checkbox for syncing mailing address with home address */}
              <div className="col-sm-12 mb-3">
                <label className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    checked={isMailingSameAsHome}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (
                        values.home_address &&
                        values.state_id &&
                        values.county_id
                      ) {
                        setIsMailingSameAsHome(isChecked);
                        if (isChecked) {
                          setMailingStatesList(statesList);
                          setMailingCountiesList(countiesList);
                          setMailingCitiesList(citiesList);
                          setMailingZipCodesList(zipCodesList);
                          setFieldValue("mailing_address", values.home_address);
                          setFieldValue("mailing_state_id", values.state_id);
                          setFieldValue("mailing_county_id", values.county_id);
                          setFieldValue("mailing_city_id", values.city_id);
                          setFieldValue(
                            "mailing_zip_code_id",
                            values.zip_code_id
                          );
                        } else {
                          setMailingCountiesList([]);
                          setMailingCitiesList([]);
                          setMailingZipCodesList([]);
                          setFieldValue("mailing_address", "");
                          setFieldValue("mailing_state_id", "");
                          setFieldValue("mailing_county_id", "");
                          setFieldValue("mailing_city_id", "");
                          setFieldValue("mailing_zip_code_id", "");
                        }
                      } else {
                        toast(
                          <CustomToast
                            variant={alertTypes.INFO}
                            message="Fill Home  Address, State and County!"
                          />
                        );
                      }
                    }}
                    className="me-2 form-check-input light-grey-background"
                  />
                  Mailing Address same as Home Address
                </label>
              </div>

              {!isMailingSameAsHome && (
                <>
                  <div className="col-sm-6">
                    <Input
                      isRequired
                      handleChange={handleChange}
                      placeholder="e.g. 1234 Main St."
                      type="text"
                      label="Mailing Address"
                      value={values.mailing_address}
                      name="mailing_address"
                      className="mb-1"
                      disabled={isMailingSameAsHome}
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="mailing_address"
                        />
                      }
                    />
                  </div>
                  <div className="col-sm-6">
                    <AsyncSelect
                      label="Mailing State"
                      isRequired
                      options={mailingStatesList?.map((el) => ({
                        value: el.id,
                        label: el.name.toUpperCase(),
                      }))}
                      loadOptions={(inputValue) =>
                        new Promise((resolve) => {
                          debouncedStatesDataCallback(inputValue, resolve);
                        })
                      }
                      selectedValue={values.mailing_state_id}
                      placeholder="Select State"
                      handleChange={(obj) => {
                        setFieldValue("mailing_state_id", obj);
                        setFieldValue("mailing_county_id", "");
                        setFieldValue("mailing_city_id", "");
                        setFieldValue("mailing_zip_code_id", "");
                        fetchMailingCounties(obj.value);
                      }}
                      disabled={isMailingSameAsHome}
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="mailing_state_id"
                        />
                      }
                    />
                  </div>
                  <div className="col-sm-6">
                    <AsyncSelect
                      selectedValue={values.mailing_county_id}
                      label="Mailing County"
                      isRequired
                      disabled={isMailingSameAsHome || !values.mailing_state_id}
                      options={mailingCountiesList?.map((el) => ({
                        value: el.id,
                        label: formatLocation(el.name),
                      }))}
                      loadOptions={(inputValue) =>
                        new Promise((resolve) => {
                          debouncedCountiesDataCallback(
                            inputValue,
                            values.mailing_state_id?.value,
                            resolve
                          );
                        })
                      }
                      placeholder="Select County"
                      handleChange={(obj) => {
                        setFieldValue("mailing_county_id", obj);
                        setFieldValue("mailing_city_id", "");
                        setFieldValue("mailing_zip_code_id", "");
                        fetchMailingCities(
                          values.mailing_state_id?.value,
                          obj.value
                        );
                      }}
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="mailing_county_id"
                        />
                      }
                    />
                  </div>
                  <div className="col-sm-6">
                    <AsyncSelect
                      label="Mailing City"
                      disabled={
                        isMailingSameAsHome || !values.mailing_county_id
                      }
                      options={mailingCitiesList.map((el) => ({
                        value: el.id,
                        label: capitalizeName(el.name),
                      }))}
                      loadOptions={(inputValue) =>
                        new Promise((resolve) => {
                          debouncedCitiesDataCallback(
                            inputValue,
                            values.mailing_state_id?.value,
                            values.mailing_county_id?.value,
                            resolve
                          );
                        })
                      }
                      selectedValue={values?.mailing_city_id}
                      placeholder="Select City"
                      handleChange={(obj) => {
                        setFieldValue("mailing_city_id", obj);
                        setFieldValue("mailing_zip_code_id", "");
                        fetchMailingZipCodes(
                          values.mailing_state_id?.value,
                          values.mailing_county_id?.value,
                          obj.value
                        );
                      }}
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="mailing_city_id"
                        />
                      }
                    />
                  </div>
                  <div>
                    <AsyncSelect
                      name="mailing_zip_code_id"
                      label="Mailing ZIP Code"
                      disabled={isMailingSameAsHome || !values.mailing_city_id}
                      options={mailingZipCodesList?.map((el) => ({
                        value: el.id,
                        label: el.code,
                      }))}
                      loadOptions={(inputValue) =>
                        new Promise((resolve) => {
                          debouncedZipCodesDataCallback(
                            inputValue,
                            values.mailing_state_id?.value,
                            values.mailing_county_id?.value,
                            values.mailing_city_id?.value,
                            resolve
                          );
                        })
                      }
                      selectedValue={values?.mailing_zip_code_id}
                      handleChange={(obj) => {
                        setFieldValue("mailing_zip_code_id", obj);
                      }}
                      className="mb-1"
                      error={
                        <ErrorMessage
                          className="error-message"
                          component="p"
                          name="mailing_zip_code_id"
                        />
                      }
                    />
                  </div>
                </>
              )}
            </div>
            <h4 className="my-4">Communication Preferences</h4>
            <div className="input-flex row">
              <div className="col-sm-6">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. English"
                  type="text"
                  label="Preferred Language Spoken"
                  value={values.preferred_spoken_lang}
                  name="preferred_spoken_lang"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="preferred_spoken_lang"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <Input
                  isRequired
                  handleChange={handleChange}
                  placeholder="e.g. Chinese"
                  type="text"
                  label="Preferred Language Written"
                  value={values.preferred_written_lang}
                  name="preferred_written_lang"
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="preferred_written_lang"
                    />
                  }
                />
              </div>
            </div>
            <h4 className="my-4">Other</h4>
            <div className="input-flex row">
              <div className="col-sm-6">
                <SelectComponent
                  isRequired
                  name="population_focus"
                  label="Population of Focus"
                  options={POPULATION_FOCUSES}
                  selectedValue={values.population_focus}
                  isMulti
                  placeholder="Select Population of Focus"
                  handleChange={(arr) => setFieldValue("population_focus", arr)}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="population_focus"
                    />
                  }
                />
              </div>
              <div className="col-sm-6">
                <SelectComponent
                  isRequired
                  label="Conditions"
                  name="conditions"
                  options={allConditions}
                  selectedValue={values.conditions}
                  isMulti
                  placeholder="Select Conditions"
                  handleChange={(arr) => setFieldValue("conditions", arr)}
                  error={
                    <ErrorMessage
                      className="error-message"
                      component="p"
                      name="conditions"
                    />
                  }
                />
              </div>
            </div>
            <div>
              <h4 className="my-4">Authorization Info</h4>
              <div className="input-flex row">
                <div className="col-sm-6">
                  <Input
                    handleChange={handleChange}
                    type="text"
                    label="Program"
                    value="ECM"
                    name="auth_program"
                    error={
                      <ErrorMessage
                        className="error-message"
                        component="p"
                        name="auth_program"
                      />
                    }
                  />
                </div>
                <div className="col-sm-6">
                  <Input
                    handleChange={handleChange}
                    placeholder="Auth Number..."
                    type="text"
                    label="Authorization Number"
                    value={values.auth_number}
                    name="auth_number"
                    error={
                      <ErrorMessage
                        className="error-message"
                        component="p"
                        name="auth_number"
                      />
                    }
                  />
                </div>
                <div className="col-sm-6">
                  <DateInput
                    handleChange={(date) =>
                      setFieldValue("auth_start_date", date)
                    }
                    value={values.auth_start_date}
                    label="Authorization Start Date"
                    name="auth_start_date"
                    placeholder="Auth Start Date.."
                    error={
                      <ErrorMessage
                        className="error-message"
                        component="p"
                        name="auth_start_date"
                      />
                    }
                  />
                </div>
                <div className="col-sm-6">
                  <DateInput
                    handleChange={(date) =>
                      setFieldValue("auth_end_date", date)
                    }
                    value={values.auth_end_date}
                    label="Authorization End Date"
                    name="auth_end_date"
                    placeholder="Auth End Date.."
                    error={
                      <ErrorMessage
                        className="error-message"
                        component="p"
                        name="auth_end_date"
                      />
                    }
                  />
                </div>
              </div>
            </div>
            {/* <AddressSync
              isMailingSameAsHome={isMailingSameAsHome}
              fetchMailingCounties={fetchMailingCounties}
              fetchMailingCities={fetchMailingCities}
              fetchMailingZipCodes={fetchMailingZipCodes}
              mailingCountiesList={mailingCountiesList}
              mailingCitiesList={mailingCitiesList}
              mailingZipCodesList={mailingZipCodesList}
            /> */}
            <hr className="custom-modal-line mt-4" />
            <div className="action gap-2">
              <button
                className="secondary-btn w-50"
                type="button"
                onClick={handleClose}
              >
                Cancel
              </button>
              <input
                className="primary-btn w-50"
                type="submit"
                name="submit"
                value={`${selectedMember ? "Edit" : "Add"} Member`}
              />
            </div>
          </form>
        </div>
      )}
    </Formik>
  );
};

export default AddQuestionForm;
